// The purchases a country return reads, grouped into one card per expense
// type (operator 2026-10-13: one flat table was too cluttered). A summary
// line says what goes in the input-VAT box; each card asks its legal
// question once, with the provision and note behind an (i); invoices sit
// underneath with what each one reclaims. Purchases without a type have
// their own card at the top.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { answerPurchases, getPurchaseRows, setPurchaseTypes } from "../../lib/api";

const fmt = (v) => new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(Number(v));

const selectCls = "border rounded px-2 py-1 text-sm max-w-full bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100";

const TypePicker = ({ groups, value, onChange, label, testid, placeholder, small }) => (
  <select value={value || ""} onChange={(e) => onChange(e.target.value)}
          aria-label={label} data-testid={testid}
          className={small ? `${selectCls} text-xs py-0.5` : selectCls}>
    <option value="">{placeholder}</option>
    {groups.map((g) => (
      <optgroup key={g.label} label={g.label}>
        {g.types.map((tp) => <option key={tp.code} value={tp.code}>{tp.label}</option>)}
      </optgroup>
    ))}
  </select>
);

// A small (i) that opens the legal note in place.
const Info = ({ title, children, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <span className="inline-flex flex-col">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
              aria-label={title} data-testid={testid}
              className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full border border-slate-400 text-[11px] leading-none opacity-80 hover:opacity-100 focus-visible:ring-2 focus-visible:ring-sky-400">
        i
      </button>
      {open && (
        <span role="note" className="mt-1 max-w-prose text-xs rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-2 py-1">
          {children}
        </span>
      )}
    </span>
  );
};

const answerText = (a, t) => (a.reclaim_pct == null
  ? a.label : `${a.label} — ${t("ui.purchase_questions.reclaim", { pct: a.reclaim_pct })}`);

const sum = (rows, f) => rows.reduce((s, r) => s + Number(r[f] || 0), 0);

// "Box 48: 124.94 -> 7,109.51" for each box the save moved. A box holding
// a figure typed on the return does not move: say so, with what the
// purchases now give. Nothing moved at all: say where to look.
const ChangeLines = ({ changes }) => {
  const { t } = useTranslation();
  if (!changes.length) return <span>{t("ui.purchase_rows.unchanged_why")}</span>;
  return changes.map((c) => (
    <span key={`${c.country}-${c.box}`} className="block font-medium">
      {c.operator_value_kept
        ? t("ui.purchase_rows.kept_change", { box: c.box, value: fmt(c.after), after: fmt(c.prefill_after) })
        : t("ui.purchase_questions.box_change", { box: c.box, before: fmt(c.before), after: fmt(c.after) })}
    </span>
  ));
};

// What a save changed: a box that moved, or a typed box whose purchases
// figure moved underneath it.
export const movedBoxes = (res) => (res?.box_changes || []).filter((c) => (
  c.operator_value_kept
    ? Number(c.prefill_before) !== Number(c.prefill_after)
    : Number(c.before) !== Number(c.after)));

// Why an invoice adds nothing (or what kind of deduction it is), from the
// return's own calculation.
const RECLAIM_LABEL = {
  foreign_vat: "ui.purchase_rows.foreign_vat",
  taxed_abroad: "ui.purchase_rows.taxed_abroad",
  import: "ui.purchase_rows.import",
  supplier_should_charge: "ui.purchase_rows.supplier_should_charge",
  undecided: "ui.purchase_rows.undecided",
  unrated: "ui.purchase_rows.unrated",
  not_on_form: "ui.purchase_rows.not_on_form",
  no_rate: "ui.purchase_rows.no_rate",
};

// What happened to the last answer saved from this card, shown right
// under the dropdown that was used (not at the top of the panel).
const SaveStatus = ({ status, testid }) => {
  const { t } = useTranslation();
  if (!status) return null;
  const tone = status.state === "error"
    ? "border-red-300 bg-red-50 text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-100"
    : status.state === "saving"
      ? "border-slate-200 bg-slate-50 text-slate-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
      : "border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-700 dark:bg-emerald-950 dark:text-emerald-100";
  return (
    <div role="status" aria-live="polite" data-testid={testid}
         className={`mt-2 text-xs rounded border px-2 py-1 ${tone}`}>
      {status.state === "saving" && t("ui.purchase_questions.saving")}
      {status.state === "error" && status.text}
      {status.state === "saved" && (
        <>
          <span className="block">✓ {status.result.what}</span>
          <ChangeLines changes={status.result.changes} />
        </>
      )}
    </div>
  );
};

// One card: every purchase of one expense type.
const TypeCard = ({ code, label, rows, busy, onAnswer, picked, setPicked, cur }) => {
  const { t } = useTranslation();
  const q = rows[0].question;
  const perRow = Boolean(q) && rows.length > 1;
  // While a save is on its way, show the answer just picked instead of
  // snapping back to the old one; cleared once the page has reloaded.
  const [local, setLocal] = useState({});
  const [status, setStatus] = useState(null);
  const answerOf = (r) => (r.key in local ? local[r.key] : (r.question?.answer || ""));
  const answers = [...new Set(rows.map(answerOf))];
  const shared = answers.length === 1 ? answers[0] : "";
  const [pending, setPending] = useState(null);
  const [pct, setPct] = useState("");
  const keys = rows.map((r) => r.key);
  const allPicked = keys.every((k) => picked.has(k));
  const toggle = (k) => setPicked((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const toggleAll = () => setPicked((s) => {
    const n = new Set(s);
    keys.forEach((k) => (allPicked ? n.delete(k) : n.add(k)));
    return n;
  });
  // Totals are in the return's currency (the per-invoice figures are
  // converted the same way as the box), so they always add up.
  const run = async (ks, c, p) => {
    setLocal((m) => ({ ...m, ...Object.fromEntries(ks.map((k) => [k, c])) }));
    setStatus({ state: "saving" });
    try {
      setStatus({ state: "saved", result: await onAnswer(ks, c, p) });
    } catch (e) {
      setStatus({ state: "error", text: e.message });
    } finally {
      setLocal({});
    }
  };
  const pick = (c) => {
    const a = q.answers.find((x) => x.code === c);
    if (a?.needs_business_use) { setPending(c); return; }
    setPending(null);
    if (c) run(keys, c, null);
  };
  const chosen = q?.answers.find((a) => a.code === (pending || shared));
  return (
    <div className="min-w-0 rounded-lg border border-slate-200 dark:border-slate-700 p-3" data-testid={`purchase-card-${code}`}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <label className="inline-flex items-center gap-2">
          <input type="checkbox" checked={allPicked} onChange={toggleAll}
                 data-testid={`purchase-card-select-${code}`}
                 aria-label={t("ui.purchase_rows.select_all_in", { type: label })} />
          <h4 className="font-semibold">{label}</h4>
        </label>
        <span className="text-xs opacity-70">{t("ui.purchase_rows.invoices", { n: rows.length })}</span>
        <span className="ml-auto text-xs tabular-nums" data-testid={`purchase-card-total-${code}`}>
          {t("ui.purchase_rows.card_total", {
            reclaim: fmt(sum(rows, "reclaim_vat")), vat: fmt(sum(rows, "vat_in_return_currency")), cur })}
        </span>
      </div>

      {q ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className="text-sm">{q.text}</span>
          {(q.note || rows[0].provision) && (
            <Info title={t("ui.purchase_rows.why")} testid={`purchase-card-info-${code}`}>
              {q.note && <span className="block">{q.note}</span>}
              {rows[0].provision && <span className="block opacity-80">{rows[0].provision}</span>}
            </Info>
          )}
          <span className="text-xs opacity-80">{t("ui.purchase_rows.answer_all", { n: rows.length })}</span>
          <select value={pending || shared} onChange={(e) => pick(e.target.value)} disabled={busy}
                  aria-label={`${q.text} — ${t("ui.purchase_rows.answer_all", { n: rows.length })}`}
                  data-testid={`purchase-card-answer-${code}`} className={selectCls}>
            <option value="">
              {answers.length > 1 ? t("ui.purchase_rows.mixed")
                : t("ui.purchase_rows.unanswered", { pct: q.unanswered_pct })}
            </option>
            {q.answers.map((a) => <option key={a.code} value={a.code}>{answerText(a, t)}</option>)}
          </select>
          {chosen?.needs_business_use && (
            <span className="inline-flex items-center gap-1">
              <input type="number" inputMode="decimal" min="0" max="100" step="any"
                     name="business_use_pct" autoComplete="off" value={pct}
                     onChange={(e) => setPct(e.target.value.trim())}
                     aria-label={t("ui.purchase_questions.business_use")}
                     className="border rounded px-2 py-1 w-20 text-base sm:text-sm bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100" />%
              <button type="button" disabled={busy || pct === "" || Number(pct) < 0 || Number(pct) > 100}
                      onClick={() => { run(keys, pending || shared, pct); setPending(null); }}
                      className="px-2 py-1 rounded bg-sky-700 text-white text-xs disabled:opacity-50">
                {t("ui.purchase_rows.save")}
              </button>
            </span>
          )}
        </div>
      ) : (
        <div className="mt-2 text-sm flex items-center">
          {t("ui.purchase_rows.fixed", { pct: rows[0].fixed_pct ?? "100" })}
          {(rows[0].fixed_note || rows[0].provision) && (
            <Info title={t("ui.purchase_rows.why")} testid={`purchase-card-info-${code}`}>
              {rows[0].fixed_note && <span className="block">{rows[0].fixed_note}</span>}
              {rows[0].provision && <span className="block opacity-80">{rows[0].provision}</span>}
            </Info>
          )}
        </div>
      )}

      <SaveStatus status={status} testid={`purchase-card-status-${code}`} />

      <div className="overflow-x-auto mt-2">
        {/* One invoice: the card-wide answer above IS its answer, so a second,
            identical picker per invoice is only shown when there are several. */}
        <table className="text-xs w-full tabular-nums">
          <thead>
            <tr className="text-left opacity-70">
              <th className="py-1 pr-2 font-normal">
                <span className="sr-only">{t("ui.purchase_rows.select_all_in", { type: label })}</span>
              </th>
              <th className="py-1 pr-3 font-normal">{t("ui.purchase_rows.col_date")}</th>
              <th className="py-1 pr-3 font-normal">{t("ui.purchase_rows.col_invoice")}</th>
              <th className="py-1 pr-3 font-normal text-right">{t("ui.purchase_rows.col_net")}</th>
              <th className="py-1 pr-3 font-normal text-right">{t("ui.purchase_rows.col_vat")}</th>
              <th className="py-1 pr-3 font-normal text-right">{t("ui.purchase_rows.col_reclaim")}</th>
              {perRow && <th className="py-1 font-normal">{t("ui.purchase_rows.col_answer")}</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-t border-slate-100 dark:border-slate-800" data-testid={`purchase-row-${r.key}`}>
                <td className="py-1 pr-2">
                  <input type="checkbox" checked={picked.has(r.key)} onChange={() => toggle(r.key)}
                         aria-label={t("ui.purchase_rows.select_row", { invoice: r.invoice_number || r.order_id })} />
                </td>
                <td className="py-1 pr-3 whitespace-nowrap">{r.invoice_date}</td>
                <td className="py-1 pr-3 break-words">
                  {r.invoice_number || r.order_id}
                  {r.description && <span className="block opacity-70">{r.description}</span>}
                </td>
                <td className="py-1 pr-3 text-right whitespace-nowrap">{fmt(r.net_amount)} {r.currency}</td>
                <td className="py-1 pr-3 text-right whitespace-nowrap">{fmt(r.vat_amount)}</td>
                <td className="py-1 pr-3 text-right whitespace-nowrap" data-testid={`purchase-row-reclaim-${r.key}`}>
                  {RECLAIM_LABEL[r.treatment] ? t(RECLAIM_LABEL[r.treatment])
                    : `${fmt(r.reclaim_vat)}${r.currency !== cur ? ` ${cur}` : ""}`}
                  {r.treatment === "self_assessed" && (
                    <span className="block opacity-70">{t("ui.purchase_rows.self_assessed")}</span>
                  )}
                </td>
                {perRow && (
                  <td className="py-1">
                    {/* One invoice at a time: overrides the card-wide answer
                        for this invoice only. Business-use % answers are set
                        with the card-wide picker above. */}
                    <select value={answerOf(r)} disabled={busy}
                            onChange={(e) => e.target.value && run([r.key], e.target.value, null)}
                            aria-label={`${q.text} — ${r.invoice_number || r.order_id}`}
                            data-testid={`purchase-row-answer-${r.key}`}
                            className={`${selectCls} text-xs py-0.5`}>
                      <option value="">{t("ui.purchase_rows.unanswered", { pct: q.unanswered_pct })}</option>
                      {q.answers.map((a) => (
                        <option key={a.code} value={a.code} disabled={a.needs_business_use}>
                          {a.needs_business_use && r.question?.business_use_pct
                            ? `${a.label} — ${r.question.business_use_pct}%` : answerText(a, t)}
                        </option>
                      ))}
                    </select>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Purchases without a type. Tick them and use the bar at the top.
const UntypedCard = ({ rows, picked, setPicked }) => {
  const { t } = useTranslation();
  const keys = rows.map((r) => r.key);
  const allPicked = keys.every((k) => picked.has(k));
  const toggle = (k) => setPicked((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const toggleAll = () => setPicked((s) => {
    const n = new Set(s);
    keys.forEach((k) => (allPicked ? n.delete(k) : n.add(k)));
    return n;
  });
  return (
    <div className="min-w-0 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50/60 dark:bg-amber-950/30 p-3" data-testid="purchase-card-untyped">
      <div className="flex flex-wrap items-baseline gap-x-3">
        <label className="inline-flex items-center gap-2">
          <input type="checkbox" checked={allPicked} onChange={toggleAll} data-testid="purchase-card-select-untyped"
                 aria-label={t("ui.purchase_rows.select_all_in", { type: t("ui.purchase_rows.untyped_title") })} />
          <h4 className="font-semibold">{t("ui.purchase_rows.untyped_title")}</h4>
        </label>
        <span className="text-xs opacity-80">{t("ui.purchase_rows.untyped_hint")}</span>
      </div>
      <ul className="mt-2 divide-y divide-amber-200 dark:divide-amber-900">
        {rows.map((r) => (
          <li key={r.key} className="py-1 flex flex-wrap items-center gap-2 text-xs tabular-nums" data-testid={`purchase-row-${r.key}`}>
            <input type="checkbox" checked={picked.has(r.key)} onChange={() => toggle(r.key)}
                   aria-label={t("ui.purchase_rows.select_row", { invoice: r.invoice_number || r.order_id })} />
            <span className="whitespace-nowrap">{r.invoice_date}</span>
            <span className="break-words">{r.invoice_number || r.order_id}</span>
            {r.description && <span className="opacity-70 break-words">{r.description}</span>}
            <span className="whitespace-nowrap">{fmt(r.net_amount)} {r.currency} · {t("ui.purchase_rows.col_vat")} {fmt(r.vat_amount)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const PurchaseRows = ({ calcId, country, onChanged, locked = false }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState(() => new Set());
  const [bulkType, setBulkType] = useState("");
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await getPurchaseRows(calcId, country);
      setData(d);
      setLoadError(false);
      const needs = (d.rows || []).some((r) => !r.expense_type || (r.question && !r.question.answer));
      setOpen((o) => (o === null ? needs : o));
    } catch {
      setLoadError(true);
    }
  }, [calcId, country]);
  useEffect(() => { load(); }, [load]);

  const labels = useMemo(() => {
    const m = {};
    (data?.groups || []).forEach((g) => g.types.forEach((tp) => { m[tp.code] = tp.label; }));
    return m;
  }, [data]);
  const order = useMemo(() => (data?.groups || []).flatMap((g) => g.types.map((tp) => tp.code)), [data]);

  // Saves, reloads the purchases and the return, and hands back what moved.
  // A type change reports in the bar at the top (where it was made); an
  // answer reports back to the card it was given in.
  const save = async (call, what, atTop, failKey = "ui.purchase_rows.save_failed") => {
    setBusy(true);
    if (atTop) { setError(null); setMsg(null); }
    try {
      const res = await call();
      const result = { what, changes: movedBoxes(res) };
      if (atTop) { setMsg(result); setPicked(new Set()); setBulkType(""); }
      await load();
      onChanged && onChanged();
      return result;
    } catch (e) {
      const text = e?.response?.data?.detail?.message || t(failKey);
      if (atTop) setError(text);
      throw new Error(text);
    } finally {
      setBusy(false);
    }
  };
  const onType = (keys, code) => code && save(
    () => setPurchaseTypes(calcId, { keys, expense_type: code }),
    t("ui.purchase_rows.saved", { n: keys.length, type: labels[code] }), true).catch(() => {});
  const onAnswer = (keys, code, pct) => save(
    () => answerPurchases(calcId, { keys, answer: code, business_use_pct: pct }),
    t("ui.purchase_rows.saved_answer", { n: keys.length }), false, "ui.purchase_rows.answer_save_failed");

  const rows = data?.rows || [];
  if (loadError && !data) {
    return (
      <p role="alert" data-testid="purchase-rows-load-error"
         className="text-sm text-red-700 dark:text-red-300 border border-red-300 rounded-lg p-3 mb-4">
        {t("ui.purchase_rows.load_failed")}
      </p>
    );
  }
  if (rows.length === 0) return null;
  const untyped = rows.filter((r) => !r.expense_type);
  const byType = {};
  rows.filter((r) => r.expense_type).forEach((r) => { (byType[r.expense_type] ||= []).push(r); });
  const typeCodes = Object.keys(byType).sort((a, b) => order.indexOf(a) - order.indexOf(b));
  const unanswered = rows.filter((r) => r.question && !r.question.answer).length;
  const s = data.summary || {};

  let status;
  if (untyped.length) status = ["amber", t("ui.purchase_rows.untyped", { n: untyped.length })];
  else if (unanswered) status = ["amber", t("ui.purchase_rows.unanswered_count", { n: unanswered })];
  else status = ["emerald", t("ui.purchase_rows.all_set")];

  return (
    <section data-testid="purchase-rows" aria-label={t("ui.purchase_rows.title")}
             className="text-sm border border-l-4 border-sky-400 rounded-lg p-3 mb-4 bg-white dark:bg-slate-900">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-semibold">{t("ui.purchase_rows.title")}</h3>
        <span data-testid="purchase-rows-status"
              className={status[0] === "amber"
                ? "text-xs rounded px-1.5 py-0.5 bg-amber-100 text-amber-900 dark:bg-amber-900 dark:text-amber-100"
                : "text-xs rounded px-1.5 py-0.5 bg-emerald-100 text-emerald-900 dark:bg-emerald-900 dark:text-emerald-100"}>
          {status[1]}
        </span>
        <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={!!open}
                data-testid="purchase-rows-toggle"
                className="ml-auto text-xs underline focus-visible:ring-2 focus-visible:ring-sky-400">
          {open ? t("ui.purchase_rows.hide") : t("ui.purchase_rows.show")}
        </button>
      </div>
      {s.box && (
        <p className="mt-1 tabular-nums" data-testid="purchase-rows-summary">
          {t("ui.purchase_rows.summary", {
            n: rows.length, box: s.box, value: fmt(s.box_value || 0), cur: s.currency })}
        </p>
      )}
      {s.operator_kept && (
        <p role="note" data-testid="purchase-rows-operator-kept"
           className="mt-1 text-xs rounded border border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100 px-2 py-1 tabular-nums">
          {t("ui.purchase_rows.operator_kept", { box: s.box, value: fmt(s.purchases_value || 0), cur: s.currency })}
        </p>
      )}

      <div aria-live="polite" data-testid="purchase-rows-saved">
        {msg && (
          <div className="text-xs mt-2 rounded border border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-700 dark:bg-emerald-950 dark:text-emerald-100 px-2 py-1">
            <span className="block">✓ {msg.what}</span>
            <ChangeLines changes={msg.changes} />
          </div>
        )}
      </div>
      {error && <p role="alert" className="text-red-700 dark:text-red-300 text-xs mt-2">{error}</p>}
      {/* A2-581 lead 28: types and answers stayed live on a signed or locked
          calc and the server refused them (api/_signoff_guard). */}
      {locked && (
        <p role="note" data-testid="purchase-rows-locked" className="text-xs mt-2 text-slate-600 dark:text-slate-300">
          {t("ui.purchase_rows.locked", { defaultValue: "Signed off or locked for filing: expense types and answers can no longer be changed here." })}
        </p>
      )}

      {open && (
        <>
          {/* One action bar for every card: tick invoices anywhere below,
              then give them all one expense type. */}
          <div className="mt-3 sticky top-0 z-10 flex flex-wrap items-center gap-2 rounded-lg border border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-950 px-3 py-2"
               data-testid="purchase-rows-bulk">
            <span className="text-xs font-medium" data-testid="purchase-rows-bulk-count">
              {picked.size === 0 ? t("ui.purchase_rows.bulk_hint")
                : t("ui.purchase_rows.bulk_selected", { n: picked.size })}
            </span>
            <TypePicker groups={data.groups} value={bulkType} onChange={setBulkType}
                        label={t("ui.purchase_rows.bulk_label")} testid="purchase-rows-bulk-type"
                        placeholder={t("ui.purchase_rows.pick")} />
            <button type="button" data-testid="purchase-rows-bulk-apply"
                    data-resolution-class="self-resolve"
                    data-resolution-endpoint="POST /api/calc/{calc_id}/purchase-types"
                    disabled={busy || locked || !bulkType || picked.size === 0}
                    onClick={() => onType([...picked], bulkType)}
                    className="px-3 py-1 rounded bg-sky-700 text-white text-sm disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-sky-400">
              {t("ui.purchase_rows.set_type_n", { n: picked.size })}
            </button>
            {picked.size > 0 && (
              <button type="button" onClick={() => setPicked(new Set())}
                      className="text-xs underline focus-visible:ring-2 focus-visible:ring-sky-400">
                {t("ui.purchase_rows.clear")}
              </button>
            )}
          </div>
          {/* minmax(0,1fr): a grid track otherwise grows to the widest table
              inside it, and the cards spill past the section's border. */}
          <div className="mt-3 grid grid-cols-[minmax(0,1fr)] gap-3">
            {untyped.length > 0 && (
              <UntypedCard rows={untyped} picked={picked} setPicked={setPicked} />
            )}
            {typeCodes.map((code) => (
              <TypeCard key={code} code={code} label={labels[code] || code} rows={byType[code]}
                        busy={busy || locked} onAnswer={onAnswer} picked={picked} setPicked={setPicked}
                        cur={s.currency} />
            ))}
          </div>
        </>
      )}
    </section>
  );
};
